<section class="ai-action-items-block">

  <!-- ── Empty state ──────────────────────────────────────────────────────── -->
  <div class="ai-empty-state" *ngIf="rows.length === 0">
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"
      stroke-linecap="round" stroke-linejoin="round" style="color: rgba(255,255,255,0.2)">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 12h8M12 8v8" />
    </svg>
    <span>No action items found for this task.</span>
  </div>

  <!-- ── Table ──────────────────────────────────────────────────────────────── -->
  <div class="ai-task-table-wrap" *ngIf="rows.length > 0">
    <table class="ai-task-table">
      <thead>
        <tr>
          <th class="ai-th-item">
            Action Item
            <span class="ai-th-count">{{ shownCount }} of {{ totalCount }}</span>
          </th>
          <th class="ai-hide-col">Assigned To</th>
          <th class="ai-hide-col">Asset</th>
          <th class="ai-th-center ai-hide-col">Status</th>
        </tr>
      </thead>
      <tbody>
        <ng-container *ngFor="let item of rows">

          <!-- ── Main row ────────────────────────────────────────────────────── -->
          <tr class="ai-task-row" tabindex="0">
            <td class="ai-td-task">
              <div class="ai-task-name-wrap">
                <button class="ai-chevron-btn" (click)="toggleExpand(rowKey(item), $event)"
                  [title]="isExpanded(rowKey(item)) ? 'Collapse' : 'Expand'">
                  <svg class="ai-chevron" [class.open]="isExpanded(rowKey(item))" width="14" height="14"
                    viewBox="0 0 10 6" fill="none">
                    <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </button>
                <div class="ai-task-info">
                  <span class="ai-task-id">{{ item.actionItemId || '—' }}</span>
                  <div class="ai-task-title-row">
                    <span class="ai-task-title">{{ item.text || '—' }}</span>
                    <div class="ai-inline-badges">
                      <span class="ai-pill" [ngClass]="badgeClass(item.status)">{{ item.status || '—' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </td>
            <td class="ai-td-asset ai-hide-col">
              <ng-container *ngIf="getAssigneeName(item) as assigneeName; else noAssigneeCell">
                <img *ngIf="getAssigneeAvatar(item); else actionInitialsCell" [src]="getAssigneeAvatar(item)"
                  [alt]="assigneeName" class="ai-avatar ai-avatar--img" />
                <ng-template #actionInitialsCell>
                  <div class="ai-avatar ai-avatar--initials" [title]="assigneeName">
                    {{ getInitials(assigneeName) }}
                  </div>
                </ng-template>
              </ng-container>
              <ng-template #noAssigneeCell><span class="ai-empty">—</span></ng-template>
            </td>
            <td class="ai-td-asset ai-hide-col">{{ item.assetName || item.assetId || '—' }}</td>
            <td class="ai-td-center ai-hide-col">
              <span [ngClass]="badgeClass(item.status)">{{ item.status || '—' }}</span>
            </td>
          </tr>

          <!-- ── Expand row ──────────────────────────────────────────────────── -->
          <tr *ngIf="isExpanded(rowKey(item))" class="ai-expand-row">
            <td colspan="3" class="ai-expand-td">
              <div class="ai-expand-content">

                <!-- Row 1: action-item own fields -->
                <div class="ai-expand-fields-row">
                  <div class="ai-expand-field" *ngIf="item.assetId">
                    <span class="ai-expand-label">Asset ID</span>
                    <span class="ai-mono">{{ item.assetId }}</span>
                  </div>
                  <div class="ai-expand-field">
                    <span class="ai-expand-label">Assigned To</span>
                    <ng-container *ngIf="getAssigneeName(item) as assigneeName; else noAssignee">
                      <img *ngIf="getAssigneeAvatar(item); else actionInitials" [src]="getAssigneeAvatar(item)"
                        [alt]="assigneeName" class="ai-avatar ai-avatar--img" />
                      <ng-template #actionInitials>
                        <div class="ai-avatar ai-avatar--initials" [title]="assigneeName">
                          {{ getInitials(assigneeName) }}
                        </div>
                      </ng-template>
                    </ng-container>
                    <ng-template #noAssignee><span class="ai-empty">—</span></ng-template>
                  </div>
                  <div class="ai-expand-field" *ngIf="item.evidenceRequired != null">
                    <span class="ai-expand-label">Evidence</span>
                    <span class="ai-expand-value" [class.tone-verified]="item.evidenceRequired"
                      [class.tone-muted]="!item.evidenceRequired">
                      {{ item.evidenceRequired ? 'Required' : 'Not required' }}
                    </span>
                  </div>
                  <div class="ai-expand-field" *ngIf="item.assetCategories?.length">
                    <span class="ai-expand-label">Categories</span>
                    <div class="ai-expand-chips-row">
                      <span class="ai-chip" *ngFor="let cat of item.assetCategories">{{ cat }}</span>
                    </div>
                  </div>
                </div>

                <!-- Row 2: task-inherited fields (projected from parent task) -->
                <div class="ai-expand-inherited"
                  *ngIf="task?.priority || task?.risk || task?.dueDate || task?.frameworks?.length || task?.technologies?.length">
                  <span class="ai-expand-inherited-label">From task</span>
                  <div class="ai-expand-fields-row">
                    <div class="ai-expand-field" *ngIf="task.priority">
                      <span class="ai-expand-label">Priority</span>
                      <span [ngClass]="badgeClass(task!.priority)">{{ task!.priority }}</span>
                    </div>
                    <div class="ai-expand-field" *ngIf="task.risk">
                      <span class="ai-expand-label">Risk</span>
                      <span [ngClass]="badgeClass(task!.risk)">{{ task!.risk }}</span>
                    </div>
                    <div class="ai-expand-field" *ngIf="task.dueDate">
                      <span class="ai-expand-label">Due Date</span>
                      <span class="ai-expand-value">{{ task!.dueDate | date:'mediumDate' }}</span>
                    </div>
                    <div class="ai-expand-field" *ngIf="task.frameworks?.length">
                      <span class="ai-expand-label">Frameworks</span>
                      <div class="ai-expand-chips-row">
                        <span class="ai-chip" *ngFor="let fw of (task!.frameworks! | slice:0:4)">{{ fw }}</span>
                        <span class="ai-chip ai-chip--more" *ngIf="task!.frameworks!.length > 4">+{{
                          task!.frameworks!.length - 4 }}</span>
                      </div>
                    </div>
                    <div class="ai-expand-field" *ngIf="task.technologies?.length">
                      <span class="ai-expand-label">Technologies</span>
                      <div class="ai-expand-chips-row">
                        <span class="ai-chip" *ngFor="let tech of (task!.technologies! | slice:0:4)">{{ tech }}</span>
                        <span class="ai-chip ai-chip--more" *ngIf="task!.technologies!.length > 4">+{{
                          task!.technologies!.length - 4 }}</span>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </td>
          </tr>

        </ng-container>
      </tbody>
    </table>
  </div>

  <button type="button" class="ai-view-all" *ngIf="rows.length > 0">
    View all
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"
      stroke-linejoin="round" aria-hidden="true">
      <path d="M3 8h10M9 4l4 4-4 4" />
    </svg>
  </button>

</section>
